export interface LauncherPoint {
  x: number;
  y: number;
}

export interface LauncherPlacement {
  docked: boolean;
  /** Fractions of the available movement area, retained across resizes. */
  point: LauncherPoint;
}

export interface LauncherBounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export const LAUNCHER_SIZE = 56;
export const LAUNCHER_ACTION_SIZE = 24;
/** The artwork's transparent lower edge leaves a small gap before the controls. */
export const LAUNCHER_ACTION_GAP = 0;
export const DEFAULT_LAUNCHER_PLACEMENT: LauncherPlacement = {
  docked: false,
  point: { x: 0, y: 1 },
};

const unit = (value: number): number => Math.max(0, Math.min(1, value));

export function launcherPixels(point: LauncherPoint, bounds: LauncherBounds): LauncherPoint {
  return {
    x: bounds.left + unit(point.x) * Math.max(0, bounds.right - bounds.left),
    y: bounds.top + unit(point.y) * Math.max(0, bounds.bottom - bounds.top),
  };
}

export function launcherFraction(point: LauncherPoint, bounds: LauncherBounds): LauncherPoint {
  return {
    x: unit((point.x - bounds.left) / Math.max(1, bounds.right - bounds.left)),
    y: unit((point.y - bounds.top) / Math.max(1, bounds.bottom - bounds.top)),
  };
}

export function readLauncherPlacement(value: string | null): LauncherPlacement | null {
  if (!value) return null;
  try {
    return normalizeLauncherPlacement(JSON.parse(value));
  } catch {
    return null;
  }
}

export function normalizeLauncherPlacement(parsed: unknown): LauncherPlacement | null {
  if (!parsed || typeof parsed !== 'object' || !('docked' in parsed) || !('point' in parsed))
    return null;
  const { docked, point } = parsed;
  if (typeof docked !== 'boolean' || !point || typeof point !== 'object') return null;
  if (!('x' in point) || !('y' in point)) return null;
  if (typeof point.x !== 'number' || typeof point.y !== 'number') return null;
  if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) return null;
  return { docked, point: { x: unit(point.x), y: unit(point.y) } };
}
